import * as Cesium from "cesium";
import { cloneEntity } from "../Utility";
import Type from "../Types";
import { computeZoomLevel } from "../MapUtility";
import { createEdgeAutoPan } from "./EdgeAutoPan";

export let arcDrawingActive = false;

// 4 point
export function CreateEntityOnCubicBezier(viewer, dataSource, options = {}) {
  arcDrawingActive = true;

  // ---- options ----
  const {
    sampleSpacing = 1,
    previewSampleSpacing = sampleSpacing,
    width = 2,
    material = Cesium.Color.YELLOW,
    chordMaterial = Cesium.Color.CYAN,
    clampToGround = false,
    onSnap = null,
    tempDataSource = null,
    tempLayerName = "__arc_preview__",
    autoRemoveTempDataSource = true,

    snappingEnabled = true,

    snappingOptions = {
      vertex: true,
      midpoint: true,
      vertexTolerancePx: 20,
      midpointTolerancePx: 20,
      previewRadiusPx: 50, // kept for compat; effective radius is dynamic
      indexCellSizeDeg: 0.0001,
      maxPreviewMarkers: 80,
      maxVerticesPerEntityForIndex: 400,

      projectionBudgetPerFrame: 300,
      prefilterFactor: 4,
      indexRebuildDebounceMs: 200,
      mppUpdateMs: 80,
      mppMoveTolPx: 10,
    },

    pushUndo = null,
    dsForUndo = null,
    cloneForUndo = cloneEntity,
  } = options;

  // ---- snap flags ----
  const SNAP_ENABLED = snappingEnabled !== false;

  const SNAP_VERTEX_ENABLED = SNAP_ENABLED && !!snappingOptions?.vertex;
  const SNAP_MID_ENABLED = SNAP_ENABLED && !!snappingOptions?.midpoint;
  const SNAP_VERTEX_TOL = snappingOptions?.vertexTolerancePx;
  const SNAP_MID_TOL = snappingOptions?.midpointTolerancePx;

  // ---- tunables ----
  const INDEX_CELL_DEG = Math.max(1e-6, Number(snappingOptions?.indexCellSizeDeg ?? 0.0001) || 0.0001);
  const MAX_PREVIEW = snappingOptions?.maxPreviewMarkers ?? 80;
  const MAX_VERTS_PER_ENTITY = snappingOptions?.maxVerticesPerEntityForIndex ?? 400;

  const PROJECTION_BUDGET_BASE = Math.max(50, snappingOptions?.projectionBudgetPerFrame ?? 300);
  const PREFILTER_FACTOR = Math.max(1, snappingOptions?.prefilterFactor ?? 4);
  const INDEX_REBUILD_DEBOUNCE_MS = Math.max(0, snappingOptions?.indexRebuildDebounceMs ?? 200);
  const MPP_UPDATE_MS = Math.max(0, snappingOptions?.mppUpdateMs ?? 80);
  const MPP_MOVE_TOL_PX = Math.max(0, snappingOptions?.mppMoveTolPx ?? 10);

  const MAX_QUERY_CELLS = 50000;
  const MAX_RAD_DEG = 30;

  // ---- materials ----
  const previewArcMaterialProp = new Cesium.PolylineDashMaterialProperty({ color: material });
  const previewChordMaterialProp = new Cesium.ColorMaterialProperty(chordMaterial || material);
  const finalSolidMaterialProp = new Cesium.ColorMaterialProperty(material);

  const SNAP_VERTEX_TOLERANCE_BASE = 17;
  const SNAP_ENTITYMID_TOLERANCE_BASE = 17;

  const SNAP_ZOOM_CONFIG = [
    { maxZoom: 18, gpuRadiusPx: 15, previewStickyRadiusPx: 17, tol: 1 },
    { maxZoom: 19, gpuRadiusPx: 15, previewStickyRadiusPx: 17, tol: 2 },
    { maxZoom: 20, gpuRadiusPx: 30, previewStickyRadiusPx: 35, tol: 3 },
    { maxZoom: 21, gpuRadiusPx: 35, previewStickyRadiusPx: 40, tol: 4 },
    { maxZoom: 22, gpuRadiusPx: 40, previewStickyRadiusPx: 45, tol: 5 },
    { maxZoom: 23, gpuRadiusPx: 45, previewStickyRadiusPx: 50, tol: 6 },
    { maxZoom: 24, gpuRadiusPx: 50, previewStickyRadiusPx: 55, tol: 7 },
    { maxZoom: 25, gpuRadiusPx: 60, previewStickyRadiusPx: 65, tol: 8 },
    { maxZoom: 35, gpuRadiusPx: 70, previewStickyRadiusPx: 75, tol: 8 },
  ];
  function getSnapZoomConfig() {
    const zoom = typeof computeZoomLevel === "function" ? computeZoomLevel(viewer) : 0;
    for (const cfg of SNAP_ZOOM_CONFIG) {
      if (zoom <= cfg.maxZoom) return cfg;
    }
    return SNAP_ZOOM_CONFIG[SNAP_ZOOM_CONFIG.length - 1];
  }

  function getSnapGpuRadiusPx() {
    return getSnapZoomConfig().gpuRadiusPx;
  }

  function getDynamicSnapTolerancePx(basePx) {
    const cfg = getSnapZoomConfig();
    if (basePx === SNAP_VERTEX_TOLERANCE_BASE) return cfg.tol;
    if (basePx === SNAP_ENTITYMID_TOLERANCE_BASE) return cfg.tol;
    return basePx;
  }

  // ---- temp datasource ----
  let previewDS = tempDataSource;
  let createdTemp = false;
  if (!previewDS) {
    previewDS = new Cesium.CustomDataSource(tempLayerName);
    viewer.dataSources.add(previewDS);
    createdTemp = true;
  }

  // ---- state ----
  let finished = false;
  let start = null;
  let control1 = null;
  let control2 = null;
  let end = null;
  let previewChord = null;
  let previewArc = null;
  let startMarker = null;
  let control1Marker = null;
  let control2Marker = null;
  let endMarker = null;

  // snap preview markers
  const snapPreviewPool = { vertex: [], mid: [] };
  let midpointSnapMarker = null;

  // mouse + preview caches
  let lastMoveWindowPos = null;
  let lastComputedWindowPos = null;
  let lastComputedArcPositions = null;
  let lastComputedChordPositions = null;
  let lastComputedCursor = null;
  let lastComputeTime = 0;
  const PREVIEW_MIN_FRAME_MS = 33; // ~30 fps
  const CURSOR_WORLD_EPS_METERS = 0.1;

  // ======================================================================
  // Spatial index (world grid)
  // ======================================================================
  const worldIndex = {
    cellDeg: INDEX_CELL_DEG,
    verts: new Map(), // key -> [{cart,lonDeg,latDeg,ent,idx,ds}]
    mids: new Map(), // key -> [{cart,lonDeg,latDeg,ent,ds}]
    totalVerts: 0,
    totalMids: 0,
  };

  function readShow(prop, time, def = true) {
    if (prop === undefined || prop === null) return def;
    if (typeof prop?.getValue === "function") {
      const v = prop.getValue(time);
      return v === undefined || v === null ? def : !!v;
    }
    return !!prop;
  }
  function isHelperEntity(ent) {
    const p = ent?.properties;
    const get = (k) => p?.[k]?.getValue?.() === true;
    return (
      get("isControlPoint") ||
      get("isInsertPoint") ||
      get("isMidpointSnapIndicator") ||
      get("isSnapPreviewVertex") ||
      get("isSnapPreviewMid") ||
      get("isDragMeasureLine") ||
      get("isDragMeasureLabel") ||
      get("isDrawingHelper") ||
      get("isDrawingPreview") ||
      get("isDrawingTemp") ||
      get("isDrawingClickMarker") ||
      get("isArcPreview")
    );
  }
  function isEntityVisible(ent, time, dsShown = true) {
    if (!dsShown) return false;
    if (!readShow(ent?.show, time, true)) return false;
    if (typeof ent?.isAvailable === "function" && !ent.isAvailable(time)) return false;
    if (ent?.polyline) return readShow(ent.polyline?.show, time, true);
    if (ent?.polygon) return readShow(ent.polygon?.show, time, true);
    if (ent?.position) {
      const gfx = [ent.point, ent.billboard, ent.label, ent.model];
      return gfx.some((g) => g && readShow(g?.show, time, true));
    }
    return false;
  }
  function forEachEntityCandidate(cb) {
    const root = viewer?.entities?.values || [];
    for (const ent of root) if (!isHelperEntity(ent)) cb(ent, null);
    const dsc = viewer?.dataSources;
    if (dsc && typeof dsc.length === "number" && typeof dsc.get === "function") {
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        const ents = ds?.entities?.values || [];
        for (const ent of ents) if (!isHelperEntity(ent)) cb(ent, ds);
      }
    }
  }
  function getPositionsFromEntity(entity, time) {
    if (entity?.polyline) {
      const arr = entity.polyline.positions?.getValue?.(time);
      return Array.isArray(arr) ? arr : [];
    }
    if (entity?.polygon) {
      const h = entity.polygon.hierarchy?.getValue?.(time);
      const raw = h?.positions || [];
      const arr = [...raw];
      if (arr.length > 2 && Cesium.Cartesian3.equals(arr[0], arr[arr.length - 1])) arr.pop();
      return arr;
    }
    if (entity?.position) {
      const p = entity.position?.getValue?.(time);
      return p ? [p] : [];
    }
    return [];
  }
  function computeEntityDerivedHeight(entity, time, excludeIndex = -1) {
    const posArr = getPositionsFromEntity(entity, time) || [];
    if (!posArr.length) return 0;

    const heights = [];
    for (let i = 0; i < posArr.length; i++) {
      if (i === excludeIndex) continue; // skip snapped vertex index
      try {
        const c = Cesium.Cartographic.fromCartesian(posArr[i]);
        heights.push(isFinite(c.height) ? c.height : 0);
      } catch {
        heights.push(0);
      }
    }
    if (!heights.length) return 0;

    const ZERO_EPS = 0.5; // m
    let zeroCount = 0;
    let sum = 0;
    for (const h of heights) {
      if (Math.abs(h) <= ZERO_EPS) zeroCount++;
      sum += h;
    }

    if (zeroCount > heights.length / 2) return 0;
    return sum / heights.length;
  }

  function computePolylineHalfwayPoint(arr) {
    if (!arr || arr.length < 2) return null;
    let total = 0;
    const segLen = [];
    for (let i = 0; i < arr.length - 1; i++) {
      const d = Cesium.Cartesian3.distance(arr[i], arr[i + 1]);
      segLen.push(d);
      total += d;
    }
    if (total === 0) return arr[0];
    const target = total / 2;
    let acc = 0;
    for (let i = 0; i < segLen.length; i++) {
      const d = segLen[i];
      if (acc + d >= target) {
        const t = (target - acc) / (d || 1);
        const aC = Cesium.Cartographic.fromCartesian(arr[i]);
        const bC = Cesium.Cartographic.fromCartesian(arr[i + 1]);
        const lon = Cesium.Math.lerp(aC.longitude, bC.longitude, t);
        const lat = Cesium.Math.lerp(aC.latitude, bC.latitude, t);
        const h = Cesium.Math.lerp(aC.height, bC.height, t);
        return Cesium.Cartesian3.fromRadians(lon, lat, h);
      }
      acc += d;
    }
    return arr[arr.length - 1];
  }
  function computePolygonCenter(arr) {
    if (!arr?.length) return null;
    const bs = Cesium.BoundingSphere.fromPoints(arr);
    const center = bs.center;
    const cCarto = Cesium.Cartographic.fromCartesian(center);
    let sumH = 0;
    for (const p of arr) sumH += Cesium.Cartographic.fromCartesian(p).height;
    const avgH = sumH / arr.length;
    return Cesium.Cartesian3.fromRadians(cCarto.longitude, cCarto.latitude, avgH);
  }
  function getEntityAnchor(entity, time) {
    if (entity?.polyline) return computePolylineHalfwayPoint(getPositionsFromEntity(entity, time));
    if (entity?.polygon) return computePolygonCenter(getPositionsFromEntity(entity, time));
    if (entity?.position) return entity.position.getValue(time);
    return null;
  }

  function cellKey(lonDeg, latDeg) {
    const ix = Math.floor(lonDeg / worldIndex.cellDeg);
    const iy = Math.floor(latDeg / worldIndex.cellDeg);
    return `${ix},${iy}`;
  }
  function pushToCell(map, lonDeg, latDeg, rec) {
    const key = cellKey(lonDeg, latDeg);
    let arr = map.get(key);
    if (!arr) {
      arr = [];
      map.set(key, arr);
    }
    arr.push(rec);
  }

  function rebuildWorldIndex() {
    worldIndex.verts.clear();
    worldIndex.mids.clear();
    worldIndex.totalVerts = 0;
    worldIndex.totalMids = 0;

    const time = viewer.clock?.currentTime;
    forEachEntityCandidate((ent, ds) => {
      const dsShown = ds ? ds.show !== false : true;
      if (!isEntityVisible(ent, time, dsShown)) return;

      const pos = getPositionsFromEntity(ent, time);
      if (pos.length) {
        const stride = Math.max(1, Math.ceil(pos.length / MAX_VERTS_PER_ENTITY));
        for (let i = 0; i < pos.length; i += stride) {
          const p = pos[i];
          const c = Cesium.Cartographic.fromCartesian(p);
          pushToCell(worldIndex.verts, Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude), {
            cart: p,
            lonDeg: Cesium.Math.toDegrees(c.longitude),
            latDeg: Cesium.Math.toDegrees(c.latitude),
            ent,
            idx: i,
            ds,
          });
          worldIndex.totalVerts++;
        }
      }

      const mid = getEntityAnchor(ent, time);
      if (mid) {
        const c = Cesium.Cartographic.fromCartesian(mid);
        pushToCell(worldIndex.mids, Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude), {
          cart: mid,
          lonDeg: Cesium.Math.toDegrees(c.longitude),
          latDeg: Cesium.Math.toDegrees(c.latitude),
          ent,
          ds,
        });
        worldIndex.totalMids++;
      }
    });
  }

  // build once
  if (SNAP_ENABLED) {
    rebuildWorldIndex();
  }

  // debounced rebuild
  let rebuildTimer = null;
  function scheduleRebuild() {
    if (!SNAP_ENABLED) return;
    if (rebuildTimer) clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(rebuildWorldIndex, INDEX_REBUILD_DEBOUNCE_MS);
  }

  if (SNAP_ENABLED) {
    try {
      viewer?.dataSources?.collectionChanged?.addEventListener?.(scheduleRebuild);
    } catch {}
    try {
      viewer?.entities?.collectionChanged?.addEventListener?.(scheduleRebuild);
    } catch {}
  }

  // ======================================================================
  // Screen helpers, MPP, camera versioning, projection cache
  // ======================================================================
  function toWin(cart) {
    const s = viewer?.scene;
    if (!s || !cart) return null;
    const v = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(s, cart) : s.cartesianToCanvasCoordinates?.(cart);
    return v ? { x: v.x, y: v.y } : null;
  }
  function pickCartesian(win) {
    if (!win) return undefined;
    const { scene } = viewer || {};
    if (!scene) return undefined;

    let res;

    if (scene.pickPositionSupported) {
      try {
        res = scene.pickPosition(win);
      } catch {
        res = undefined;
      }
    }

    if (!Cesium.defined(res)) {
      res = scene.camera?.pickEllipsoid?.(win, scene.globe?.ellipsoid);
    }

    return Cesium.defined(res) ? res : undefined;
  }

  // MPP cache
  let lastMpp = 1.0,
    lastMppTime = 0,
    lastMppWin = { x: -1e9, y: -1e9 };
  function estimateMetersPerPixel(mouse) {
    const now = Date.now();
    const moved = Math.hypot(mouse.x - lastMppWin.x, mouse.y - lastMppWin.y);
    if (now - lastMppTime < MPP_UPDATE_MS && moved < MPP_MOVE_TOL_PX) return lastMpp;
    const p0 = pickCartesian(mouse);
    if (!p0) return lastMpp;
    const p1 = pickCartesian({ x: mouse.x + 1, y: mouse.y });
    const p2 = pickCartesian({ x: mouse.x, y: mouse.y + 1 });
    let d = 0,
      n = 0;
    if (p1) {
      d += Cesium.Cartesian3.distance(p0, p1);
      n++;
    }
    if (p2) {
      d += Cesium.Cartesian3.distance(p0, p2);
      n++;
    }
    if (n === 0) return lastMpp;
    lastMpp = d / n;
    lastMppTime = now;
    lastMppWin = { x: mouse.x, y: mouse.y };
    return lastMpp;
  }
  function getPreviewRadiusPx() {
    const h = viewer?.camera?.positionCartographic?.height;
    if (!isFinite(h)) return 10;
    const zoom = 19 - Math.log2(Math.max(1, h) / 1000);
    const z = Math.max(0, Math.min(30, zoom));
    if (z < 12) return 3;
    if (z > 20) return 25;
    return 3 + ((z - 12) * (25 - 3)) / (20 - 12);
  }

  // camera version increments on change
  let cameraVersion = 1;
  let cameraChangedCb = null;
  let lastCameraDirty = 0;
  const CAMERA_DIRTY_THROTTLE_MS = 80;
  cameraChangedCb = () => {
    const now = Date.now();
    if (now - lastCameraDirty > CAMERA_DIRTY_THROTTLE_MS) {
      lastCameraDirty = now;
      cameraVersion++;
      try {
        viewer.scene.requestRender?.();
      } catch {}
    }
  };

  if (SNAP_ENABLED) {
    try {
      viewer?.camera?.changed?.addEventListener?.(cameraChangedCb);
    } catch {}
  }

  // projection cache keyed by Cartesian3 (per cameraVersion)
  const projCache = new WeakMap(); // cart -> { ver, x, y }
  function projectPointCached(cart, budget) {
    if (!cart) return null;
    const cached = projCache.get(cart);
    if (cached && cached.ver === cameraVersion) return { x: cached.x, y: cached.y };
    if (budget.left <= 0) return null;
    const p = toWin(cart);
    budget.left--;
    if (!p) return null;
    projCache.set(cart, { ver: cameraVersion, x: p.x, y: p.y });
    return p;
  }

  // ======================================================================
  // Local candidate query (with caches and Top-K)
  // ======================================================================
  function queryIndexBox(lonDeg, latDeg, radLonDeg, radLatDeg) {
    if (!isFinite(lonDeg) || !isFinite(latDeg) || !isFinite(radLonDeg) || !isFinite(radLatDeg)) return { verts: [], mids: [] };
    if (!(worldIndex.cellDeg > 0)) return { verts: [], mids: [] };

    let ix0 = Math.floor((lonDeg - radLonDeg) / worldIndex.cellDeg);
    let iy0 = Math.floor((latDeg - radLatDeg) / worldIndex.cellDeg);
    let ix1 = Math.floor((lonDeg + radLonDeg) / worldIndex.cellDeg);
    let iy1 = Math.floor((latDeg + radLatDeg) / worldIndex.cellDeg);

    const nx = ix1 - ix0 + 1,
      ny = iy1 - iy0 + 1;
    if (!isFinite(nx) || !isFinite(ny) || nx <= 0 || ny <= 0 || nx * ny > MAX_QUERY_CELLS) return { verts: [], mids: [] };

    const verts = [],
      mids = [];
    for (let ix = ix0; ix <= ix1; ix++) {
      for (let iy = iy0; iy <= iy1; iy++) {
        const key = `${ix},${iy}`;
        const vArr = worldIndex.verts.get(key);
        const mArr = worldIndex.mids.get(key);
        if (vArr && vArr.length) for (let i = 0; i < vArr.length; i++) verts.push(vArr[i]);
        if (mArr && mArr.length) for (let i = 0; i < mArr.length; i++) mids.push(mArr[i]);
      }
    }
    return { verts, mids, key: `${ix0},${iy0},${ix1},${iy1}` };
  }

  // cell window reuse cache
  let lastLocalWin = null; // { key, pxRadius, lonDeg, latDeg, mpp, verts, mids, cameraVer }
  function sameLocalWindow(a, b) {
    if (!a || !b) return false;
    if (a.key !== b.key) return false;
    if (a.cameraVer !== b.cameraVer) return false;
    // tolerate small MPP change
    const ratio = Math.max(a.mpp, b.mpp) / Math.max(1e-6, Math.min(a.mpp, b.mpp));
    if (ratio > 1.12) return false;
    return true;
  }

  // Top-K helper without sorting (squared distances)
  function pushTopK(list, item, k, x, y) {
    const dx = x - item.p2d.x,
      dy = y - item.p2d.y;
    const d2 = dx * dx + dy * dy;
    if (!Number.isFinite(d2)) return;

    if (list.length < k) {
      item.d2 = d2;
      list.push(item);
      return;
    }
    // find worst once per push (K is small)
    let worstIdx = 0,
      worstD2 = list[0].d2;
    for (let i = 1; i < list.length; i++) {
      if (list[i].d2 > worstD2) {
        worstD2 = list[i].d2;
        worstIdx = i;
      }
    }
    if (d2 < worstD2) {
      item.d2 = d2;
      list[worstIdx] = item;
    }
  }

  function prefilterByGeoDistance(list, lonDeg, latDeg, takeN) {
    if (!list || list.length <= takeN) return list || [];
    const cosLat = Math.cos(Cesium.Math.toRadians(latDeg));
    // cheap squared-degree sort
    const scored = list.map((rec) => {
      const dx = (rec.lonDeg - lonDeg) * cosLat;
      const dy = rec.latDeg - latDeg;
      return { rec, s: dx * dx + dy * dy };
    });
    scored.sort((a, b) => a.s - b.s);
    const out = [];
    for (let i = 0; i < takeN; i++) out.push(scored[i].rec);
    return out;
  }

  function getNearbyScreenCandidates(mouse) {
    const candidates = { vertices: [], mids: [] };
    const cart = pickCartesian(mouse);
    if (!cart) return candidates;

    const c = Cesium.Cartographic.fromCartesian(cart);
    const lonDeg = Cesium.Math.toDegrees(c.longitude);
    const latDeg = Cesium.Math.toDegrees(c.latitude);

    const mpp = estimateMetersPerPixel(mouse) || 1.0;
    const tolVertexPx = SNAP_VERTEX_ENABLED ? getDynamicSnapTolerancePx(SNAP_VERTEX_TOLERANCE_BASE) : 0;
    const tolMidPx = SNAP_MID_ENABLED ? getDynamicSnapTolerancePx(SNAP_ENTITYMID_TOLERANCE_BASE) : 0;

    const pxRadius = Math.max(getSnapGpuRadiusPx(), tolVertexPx, tolMidPx);

    const rMeters = pxRadius * mpp * 1.2;
    const metersPerDegLat = 111320;
    const metersPerDegLon = Math.max(1e-6, metersPerDegLat * Math.cos(Cesium.Math.toRadians(latDeg)));
    let radLatDeg = Math.min(rMeters / metersPerDegLat, MAX_RAD_DEG);
    let radLonDeg = Math.min(rMeters / metersPerDegLon, MAX_RAD_DEG);

    // shrink if too many cells
    let nxEst = Math.max(1, Math.ceil((2 * radLonDeg) / worldIndex.cellDeg));
    let nyEst = Math.max(1, Math.ceil((2 * radLatDeg) / worldIndex.cellDeg));
    const cellsEst = nxEst * nyEst;
    if (cellsEst > MAX_QUERY_CELLS) {
      const scale = Math.sqrt(cellsEst / MAX_QUERY_CELLS);
      radLonDeg /= scale;
      radLatDeg /= scale;
    }

    // build or reuse cell window
    const box = queryIndexBox(lonDeg, latDeg, radLonDeg * 1.5, radLatDeg * 1.5);
    const windowInfo = { key: box.key, pxRadius, lonDeg, latDeg, mpp, cameraVer: cameraVersion };

    // limit the expensive projection calls
    const budget = { left: PROJECTION_BUDGET_BASE };

    // prefilter by geo distance
    const preN = Math.max(MAX_PREVIEW * PREFILTER_FACTOR, MAX_PREVIEW + 10);
    const vertsPref = SNAP_VERTEX_ENABLED ? prefilterByGeoDistance(box.verts || [], lonDeg, latDeg, preN) : [];
    const midsPref = SNAP_MID_ENABLED ? prefilterByGeoDistance(box.mids || [], lonDeg, latDeg, preN) : [];

    // viewport + margin
    const canvas = viewer && (viewer.canvas || viewer.scene?.canvas);
    const vw = canvas?.clientWidth || viewer.scene?.drawingBufferWidth || 0;
    const vh = canvas?.clientHeight || viewer.scene?.drawingBufferHeight || 0;
    const margin = pxRadius + 32;
    const time = viewer.clock?.currentTime;

    // try to reuse unprojected sets when possible
    let sourceVerts = vertsPref;
    let sourceMids = midsPref;

    // project only when needed and count cache misses toward budget
    // vertices
    if (SNAP_VERTEX_ENABLED && budget.left > 0) {
      const topK = [];
      const K = Math.min(MAX_PREVIEW, 64); // hard cap for selection
      for (let i = 0; i < sourceVerts.length && budget.left >= 0; i++) {
        const v = sourceVerts[i];
        const dsShown = v.ds ? v.ds.show !== false : true;
        if (!isEntityVisible(v.ent, time, dsShown)) continue;

        const p2d = projectPointCached(v.cart, budget);
        if (!p2d) continue; // budget may be 0
        if (p2d.x < -margin || p2d.x > vw + margin || p2d.y < -margin || p2d.y > vh + margin) continue;

        // Only consider within radius box first (cheap)
        const dx = mouse.x - p2d.x,
          dy = mouse.y - p2d.y;
        const r2 = dx * dx + dy * dy;
        const tol = pxRadius * pxRadius;
        if (r2 <= tol) pushTopK(topK, { p: v.cart, p2d, ent: v.ent, index: v.idx }, K, mouse.x, mouse.y);
      }
      candidates.vertices = topK;
    }

    // mids
    if (SNAP_MID_ENABLED && budget.left > 0) {
      const topK = [];
      const K = Math.min(MAX_PREVIEW, 64);
      for (let i = 0; i < sourceMids.length && budget.left >= 0; i++) {
        const m = sourceMids[i];
        const dsShown = m.ds ? m.ds.show !== false : true;
        if (!isEntityVisible(m.ent, time, dsShown)) continue;

        const p2d = projectPointCached(m.cart, budget);
        if (!p2d) continue;
        if (p2d.x < -margin || p2d.x > vw + margin || p2d.y < -margin || p2d.y > vh + margin) continue;

        const dx = mouse.x - p2d.x,
          dy = mouse.y - p2d.y;
        const r2 = dx * dx + dy * dy;
        const tol = pxRadius * pxRadius;
        if (r2 <= tol) pushTopK(topK, { p: m.cart, p2d, ent: m.ent }, K, mouse.x, mouse.y);
      }
      candidates.mids = topK;
    }

    return candidates;
  }

  function findVertexSnapLocal(mouse, local) {
    if (!SNAP_VERTEX_ENABLED || !local?.vertices?.length || !mouse) return null;
    let best = null;
    const tolPx = getDynamicSnapTolerancePx(SNAP_VERTEX_TOLERANCE_BASE);
    const minx = mouse.x - tolPx,
      maxx = mouse.x + tolPx;
    const miny = mouse.y - tolPx,
      maxy = mouse.y + tolPx;
    const tol2 = tolPx * tolPx;
    for (const v of local.vertices) {
      const { x, y } = v.p2d;
      if (x < minx || x > maxx || y < miny || y > maxy) continue;
      const dx = mouse.x - x,
        dy = mouse.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 > tol2) continue;
      if (!best || d2 < best.dist2) best = { dist2: d2, cartesian: v.p, vertex: v };
    }
    return best;
  }
  function findMidSnapLocal(mouse, local) {
    if (!SNAP_MID_ENABLED || !local?.mids?.length || !mouse) return null;
    let best = null;
    const tolPx = getDynamicSnapTolerancePx(SNAP_ENTITYMID_TOLERANCE_BASE);
    const minx = mouse.x - tolPx,
      maxx = mouse.x + tolPx;
    const miny = mouse.y - tolPx,
      maxy = mouse.y + tolPx;
    const tol2 = tolPx * tolPx;

    for (const m of local.mids) {
      const { x, y } = m.p2d;
      if (x < minx || x > maxx || y < miny || y > maxy) continue;
      const dx = mouse.x - x,
        dy = mouse.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 > tol2) continue;
      if (!best || d2 < best.dist2) best = { dist2: d2, cartesian: m.p, mid: m };
    }
    return best;
  }

  // ======================================================================
  // Snap markers in previewDS
  // ======================================================================
  function createVertexPreviewMarker() {
    const ent = previewDS.entities.add({
      position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
      point: {
        color: Cesium.Color.fromCssColorString("#00e5ff").withAlpha(0.95),
        pixelSize: 10,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
        outlineWidth: 1.5,
      },
      show: false,
      properties: new Cesium.PropertyBag({ isSnapPreviewVertex: true, isDrawingHelper: true }),
    });
    snapPreviewPool.vertex.push(ent);
    return ent;
  }
  function createMidPreviewMarker() {
    const ent = previewDS.entities.add({
      position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
      point: {
        color: Cesium.Color.fromCssColorString("#ff4dd2").withAlpha(0.95),
        pixelSize: 12,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
        outlineWidth: 1.5,
      },
      show: false,
      properties: new Cesium.PropertyBag({ isSnapPreviewMid: true, isDrawingHelper: true }),
    });
    snapPreviewPool.mid.push(ent);
    return ent;
  }
  function showMidpointSnapIndicator(position) {
    if (!midpointSnapMarker) {
      midpointSnapMarker = previewDS.entities.add({
        position,
        point: {
          color: Cesium.Color.fromCssColorString("#ff69b4").withAlpha(0.95),
          pixelSize: 12,
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
          outlineWidth: 1.5,
        },
        properties: new Cesium.PropertyBag({ isMidpointSnapIndicator: true, isDrawingHelper: true }),
      });
    } else {
      midpointSnapMarker.position = position;
      midpointSnapMarker.show = true;
    }
  }
  function hideMidpointSnapIndicator() {
    if (midpointSnapMarker) midpointSnapMarker.show = false;
  }
  function hideAllSnapPreviewMarkers() {
    for (const e of snapPreviewPool.vertex) e.show = false;
    for (const e of snapPreviewPool.mid) e.show = false;
    hideMidpointSnapIndicator();
  }

  const MARKER_UPDATE_MS = 40;
  let lastMarkerUpdateTime = 0;
  function updateSnapPreviewMarkers(mouse, local) {
    if (!SNAP_ENABLED) {
      hideAllSnapPreviewMarkers();
      return;
    }
    if (!mouse || !local) {
      hideAllSnapPreviewMarkers();
      return;
    }
    const now = Date.now();
    if (now - lastMarkerUpdateTime < MARKER_UPDATE_MS && lastMoveWindowPos && Math.hypot(mouse.x - lastMoveWindowPos.x, mouse.y - lastMoveWindowPos.y) < 2) return;

    const closeVertices = local.vertices || [];
    const closeMids = local.mids || [];

    while (snapPreviewPool.vertex.length < closeVertices.length && snapPreviewPool.vertex.length + snapPreviewPool.mid.length < MAX_PREVIEW) createVertexPreviewMarker();
    while (snapPreviewPool.mid.length < closeMids.length && snapPreviewPool.vertex.length + snapPreviewPool.mid.length < MAX_PREVIEW) createMidPreviewMarker();

    for (let i = 0; i < snapPreviewPool.vertex.length; i++) {
      const marker = snapPreviewPool.vertex[i];
      if (i < closeVertices.length) {
        marker.position = closeVertices[i].p;
        marker.show = true;
      } else marker.show = false;
    }
    for (let i = 0; i < snapPreviewPool.mid.length; i++) {
      const marker = snapPreviewPool.mid[i];
      if (i < closeMids.length) {
        marker.position = closeMids[i].p;
        marker.show = true;
      } else marker.show = false;
    }
    lastMarkerUpdateTime = now;
  }

  // ======================================================================
  // Arc math
  // ======================================================================
  const SCRATCH = {
    c0: new Cesium.Cartesian3(),
    c1: new Cesium.Cartesian3(),
    c2: new Cesium.Cartesian3(),
    c3: new Cesium.Cartesian3(), // new
    geo: new Cesium.EllipsoidGeodesic(),
  };

  function createPointMarker(position) {
    if (!position) return null;
    const heightRef = clampToGround ? Cesium.HeightReference.CLAMP_TO_GROUND : Cesium.HeightReference.NONE;
    return previewDS.entities.add({
      position,
      point: { pixelSize: 10, color: Cesium.Color.YELLOW, outlineColor: Cesium.Color.BLACK, outlineWidth: 2, heightReference: heightRef },
      properties: new Cesium.PropertyBag({ isArcPreview: true, isDrawingHelper: true }),
    });
  }

  function bezierPointAtCubic(P0, P1, P2, P3, u) {
    const oneMinus = 1 - u;
    const oneMinus2 = oneMinus * oneMinus;
    const u2 = u * u;

    const a = Cesium.Cartesian3.multiplyByScalar(P0, oneMinus2 * oneMinus, SCRATCH.c0); // (1-u)^3 P0
    const b = Cesium.Cartesian3.multiplyByScalar(P1, 3 * oneMinus2 * u, SCRATCH.c1); // 3(1-u)^2 u P1
    const c = Cesium.Cartesian3.multiplyByScalar(P2, 3 * oneMinus * u2, SCRATCH.c2); // 3(1-u) u^2 P2
    const d = Cesium.Cartesian3.multiplyByScalar(P3, u2 * u, SCRATCH.c3); // u^3 P3

    const ab = Cesium.Cartesian3.add(a, b, new Cesium.Cartesian3());
    const cd = Cesium.Cartesian3.add(c, d, new Cesium.Cartesian3());
    return Cesium.Cartesian3.add(ab, cd, ab);
  }

  function geodesicDistanceOnEllipsoid(A, B) {
    const aC = Cesium.Cartographic.fromCartesian(A);
    const bC = Cesium.Cartographic.fromCartesian(B);
    SCRATCH.geo.setEndPoints(aC, bC);
    return SCRATCH.geo.surfaceDistance || 0;
  }
  function computeArcPoints3D_adaptive(P0, P1, P2, P3, spacing = 1.0) {
    if (!P0 || !P1 || !P2 || !P3) return [P0, P3].filter(Boolean);
    if (spacing <= 0) spacing = 1.0;

    const stack = [[0.0, 1.0]];
    const result = [P0];
    const MAX_ITERS = 1 << 15;
    let iters = 0;

    while (stack.length && iters++ < MAX_ITERS) {
      const [u0, u1] = stack.pop();
      const A = bezierPointAtCubic(P0, P1, P2, P3, u0);
      const B = bezierPointAtCubic(P0, P1, P2, P3, u1);
      const d = geodesicDistanceOnEllipsoid(A, B);
      if (d <= spacing * 1.2) {
        result.push(B);
      } else {
        const umid = (u0 + u1) * 0.5;
        stack.push([umid, u1], [u0, umid]);
      }
    }

    const last = result[result.length - 1];
    if (!last || !Cesium.Cartesian3.equals(last, P3)) {
      result[result.length - 1] = P3;
    }
    return result;
  }

  // ======================================================================
  // Snap-aware pick
  // ======================================================================
  function pickOrSnapFromWindow(mouse) {
    if (!mouse) return null;

    if (!SNAP_ENABLED) {
      hideAllSnapPreviewMarkers();
      return pickCartesian(mouse) || null;
    }

    const local = getNearbyScreenCandidates(mouse);

    const time = viewer.clock?.currentTime;
    let snapped = null;

    const vBest = findVertexSnapLocal(mouse, local);
    if (vBest) {
      const ent = vBest.vertex.ent;
      const idx = vBest.vertex.index ?? -1;
      const carto = Cesium.Cartographic.fromCartesian(vBest.cartesian);
      const h = computeEntityDerivedHeight(ent, time, idx);
      snapped = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h);
    } else {
      const mBest = findMidSnapLocal(mouse, local);
      if (mBest) {
        const ent = mBest.mid.ent;
        const carto = Cesium.Cartographic.fromCartesian(mBest.cartesian);
        const h = computeEntityDerivedHeight(ent, time, -1);
        snapped = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h);
      }
    }

    if (snapped && !vBest && SNAP_MID_ENABLED) showMidpointSnapIndicator(snapped);
    else hideMidpointSnapIndicator();

    updateSnapPreviewMarkers(mouse, local);
    if (snapped) return snapped;

    return pickCartesian(mouse) || null;
  }

  // ======================================================================
  // Preview entities
  // ======================================================================
  const chordCallback = new Cesium.CallbackProperty(() => {
    if (finished || !start) return null;

    const pts = [];
    pts.push(start);
    if (control1) pts.push(control1);
    if (control2) pts.push(control2);

    let lastPoint = end;

    if (!lastPoint && lastMoveWindowPos) {
      let p = pickOrSnapFromWindow(lastMoveWindowPos);
      if (!p) return pts.length >= 2 ? pts : null;
      if (onSnap) {
        const sp = onSnap(p);
        if (sp) p = sp;
      }
      lastPoint = p;
    }

    if (lastPoint) pts.push(lastPoint);
    if (pts.length < 2) return null;
    return pts;
  }, false);

  const arcCallback = new Cesium.CallbackProperty(() => {
    if (finished || !start) return null;

    const now = Date.now();

    if (start && control1 && control2 && !end && lastMoveWindowPos) {
      if (
        lastComputedArcPositions &&
        lastComputedWindowPos &&
        now - lastComputeTime < PREVIEW_MIN_FRAME_MS &&
        lastComputedWindowPos.x === lastMoveWindowPos.x &&
        lastComputedWindowPos.y === lastMoveWindowPos.y
      ) {
        return lastComputedArcPositions;
      }

      let cursor = pickOrSnapFromWindow(lastMoveWindowPos);
      if (!cursor) return null;
      if (onSnap) {
        const sp = onSnap(cursor);
        if (sp) cursor = sp;
      }

      if (lastComputedCursor && Cesium.Cartesian3.distance(lastComputedCursor, cursor) < CURSOR_WORLD_EPS_METERS && lastComputedArcPositions) {
        lastComputedWindowPos = { x: lastMoveWindowPos.x, y: lastMoveWindowPos.y };
        lastComputeTime = now;
        return lastComputedArcPositions;
      }

      try {
        const positions = computeArcPoints3D_adaptive(start, control1, control2, cursor, previewSampleSpacing);
        lastComputedWindowPos = { x: lastMoveWindowPos.x, y: lastMoveWindowPos.y };
        lastComputedCursor = cursor;
        lastComputeTime = now;
        lastComputedArcPositions = positions;
        return positions;
      } catch {
        return null;
      }
    }

    if (start && control1 && control2 && end) {
      if (lastComputedArcPositions) return lastComputedArcPositions;
      try {
        const positions = computeArcPoints3D_adaptive(start, control1, control2, end, previewSampleSpacing);
        lastComputedArcPositions = positions;
        return positions;
      } catch {
        return null;
      }
    }

    return null;
  }, false);

  previewChord = previewDS.entities.add({
    polyline: { positions: chordCallback, width: 1, clampToGround, material: previewChordMaterialProp },
    properties: new Cesium.PropertyBag({ isArcPreview: true, isDrawingHelper: true }),
  });
  previewArc = previewDS.entities.add({
    polyline: { positions: arcCallback, width, clampToGround, material: previewArcMaterialProp },
    properties: new Cesium.PropertyBag({ isArcPreview: true, isDrawingHelper: true }),
  });

  // ======================================================================
  // Input handlers
  // ======================================================================
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);

  const edgeAutoPan = createEdgeAutoPan({
    viewer,

    edgePx: 60,

    speedMin: 5,
    speedMax: 60,
    speedZGt24: 5,
    speedZ22_24: 20,
    speedZLt22: 50,

    getZoomLevel: () => computeZoomLevel(viewer),
  });

  function finalizeWithEndPoint(endPoint, autoSave) {
    if (!endPoint) return;

    end = endPoint;
    endMarker ? (endMarker.position = end) : (endMarker = createPointMarker(end));

    try {
      const arcPositions = computeArcPoints3D_adaptive(start, control1, control2, end, sampleSpacing);

      const all = dataSource?.entities?.values || [];
      let assetType = null;
      for (const e of all) {
        if (e?.polyline && e?.geojsonType && Object.values(Type.ASSET).includes(e.geojsonType)) {
          assetType = e.geojsonType;
          break;
        }
      }
      if (!assetType) assetType = Type.ASSET.GEOJSON;

      const now = Cesium.JulianDate.now();
      let maxId = 0;
      for (const e of all) {
        const v = e?.properties?.getValue?.(now) || {};
        const n = Number(v?.id);
        if (Number.isFinite(n) && n > maxId) maxId = n;
      }
      const newId = maxId + 2;
      const propBag = new Cesium.PropertyBag({ id: newId });

      const finalEntity = dataSource.entities.add({
        polyline: { positions: arcPositions, width, clampToGround, material: finalSolidMaterialProp },
        geojsonType: assetType,
        properties: propBag,
      });

      try {
        if (pushUndo) {
          const snapshot = cloneForUndo(finalEntity);
          pushUndo({ type: "add", entitySnapshot: snapshot, entityId: finalEntity.id, dsRef: dsForUndo || dataSource });
        }
      } catch {}

      const geoCoords = arcPositions.map((p) => {
        const carto = Cesium.Cartographic.fromCartesian(p);
        return [Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude), carto.height || 0];
      });

      const geojson = {
        type: "Feature",
        geometry: { type: "LineString", coordinates: geoCoords },
        properties: { id: newId },
      };

      try {
        if (dataSource && dataSource.entities.contains(finalEntity)) dataSource.entities.remove(finalEntity);
      } catch {}

      cleanup();
      resolvePromise({ entity: finalEntity, geojson, autoSave: !!autoSave });
    } catch (e) {
      cleanup();
      rejectPromise(e);
    }
  }

  handler.setInputAction((click) => {
    if (finished) return;
    const mouse = click.position;
    let p = pickOrSnapFromWindow(mouse);
    if (!p) return;
    if (onSnap) {
      const sp = onSnap(p);
      if (sp) p = sp;
    }

    if (!start) {
      start = p;
      startMarker ? (startMarker.position = start) : (startMarker = createPointMarker(start));
      try {
        viewer.scene.requestRender();
      } catch {}
      return;
    }

    if (!control1) {
      control1 = p;
      control1Marker ? (control1Marker.position = control1) : (control1Marker = createPointMarker(control1));
      try {
        viewer.scene.requestRender();
      } catch {}
      return;
    }

    if (!control2) {
      control2 = p;
      control2Marker ? (control2Marker.position = control2) : (control2Marker = createPointMarker(control2));
      try {
        viewer.scene.requestRender();
      } catch {}
      return;
    }

    if (!end) {
      end = p;
      endMarker ? (endMarker.position = end) : (endMarker = createPointMarker(end));
    }

    finalizeWithEndPoint(end, false);
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  handler.setInputAction((move) => {
    if (finished) return;
    lastMoveWindowPos = move.endPosition;

    edgeAutoPan.setMousePosition(lastMoveWindowPos);
    edgeAutoPan.start();

    if (lastMoveWindowPos) {
      if (SNAP_ENABLED) {
        const mouse = { x: lastMoveWindowPos.x, y: lastMoveWindowPos.y };
        const local = getNearbyScreenCandidates(mouse);
        updateSnapPreviewMarkers(mouse, local);
      } else {
        hideAllSnapPreviewMarkers();
      }
      try {
        viewer.scene.requestRender();
      } catch {}
    }
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    cleanup();
    rejectPromise(new Error("Arc drawing canceled by user."));
  }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      cleanup();
      rejectPromise(new Error("Arc drawing canceled (Esc)."));
    }
    if (e.key === "s" || e.key === "S") {
      if (!start || !control1 || !control2 || end) return;
      if (!lastMoveWindowPos) return;

      e.preventDefault();
      e.stopPropagation();

      let p = pickOrSnapFromWindow(lastMoveWindowPos);
      if (!p) return;
      if (onSnap) {
        const sp = onSnap(p);
        if (sp) p = sp;
      }

      finalizeWithEndPoint(p, true);
    }
  };
  window.addEventListener("keydown", onKeyDown);

  // ======================================================================
  // Promise + cleanup
  // ======================================================================
  let _resolve,
    _reject,
    _settled = false;
  const promise = new Promise((res, rej) => {
    _resolve = res;
    _reject = rej;
  });
  function resolvePromise(v) {
    if (!_settled) {
      _settled = true;
      _resolve(v);
    }
  }
  function rejectPromise(e) {
    if (!_settled) {
      _settled = true;
      _reject(e);
    }
  }

  function safeRemoveEntity(ds, ent) {
    if (!ds || !ent) return;
    try {
      if (ds.entities.contains(ent)) ds.entities.remove(ent);
    } catch {}
  }
  function cleanup() {
    if (finished) return;
    finished = true;
    arcDrawingActive = false;

    try {
      handler && !handler.isDestroyed() && handler.destroy();
    } catch {}
    window.removeEventListener("keydown", onKeyDown);

    try {
      edgeAutoPan.destroy();
    } catch {}

    try {
      if (cameraChangedCb) {
        viewer?.camera?.changed?.removeEventListener?.(cameraChangedCb);
      }
    } catch {}

    try {
      viewer?.dataSources?.collectionChanged?.removeEventListener?.(scheduleRebuild);
    } catch {}

    try {
      viewer?.entities?.collectionChanged?.removeEventListener?.(scheduleRebuild);
    } catch {}

    if (rebuildTimer) {
      clearTimeout(rebuildTimer);
      rebuildTimer = null;
    }

    safeRemoveEntity(previewDS, previewChord);
    safeRemoveEntity(previewDS, previewArc);
    safeRemoveEntity(previewDS, startMarker);
    safeRemoveEntity(previewDS, control1Marker);
    safeRemoveEntity(previewDS, control2Marker);
    safeRemoveEntity(previewDS, endMarker);

    try {
      for (const e of snapPreviewPool.vertex) safeRemoveEntity(previewDS, e);
      for (const e of snapPreviewPool.mid) safeRemoveEntity(previewDS, e);
      safeRemoveEntity(previewDS, midpointSnapMarker);
    } catch {}

    previewChord = null;
    previewArc = null;
    startMarker = null;
    control1Marker = null;
    control2Marker = null;
    endMarker = null;

    start = null;
    control1 = null;
    control2 = null;
    end = null;

    lastMoveWindowPos = null;
    lastComputedWindowPos = null;
    lastComputedArcPositions = null;
    lastComputedChordPositions = null;
    lastComputedCursor = null;

    if (createdTemp && autoRemoveTempDataSource) {
      try {
        if (!viewer.isDestroyed() && viewer.dataSources.contains(previewDS)) viewer.dataSources.remove(previewDS, true);
      } catch {}
    } else {
      try {
        previewDS.entities.removeAll();
      } catch {}
    }

    try {
      !viewer.isDestroyed() && viewer.scene.requestRender();
    } catch {}
  }

  let cancelFn;
  cancelFn = () => {
    cleanup();
    rejectPromise("cancelled");
  };
  promise.cancel = () => {
    if (cancelFn) cancelFn();
  };

  return promise;
}
